import picksTemplate from "./picks.json";
import {
    buildRoundDisplayLines,
    completedRoundGrossStrokes,
} from "./espnRoundDetails";
import { buildPairingGroups } from "./pairingGroups";

function totalToParFromLinescores(player) {
    return (player.linescores || []).reduce((total, scores) => {
        if (!scores.displayValue) return total;
        const actualValue =
            scores.displayValue === "E" || scores.displayValue === "-"
                ? 0
                : parseInt(scores.displayValue, 10);
        return Number.isNaN(actualValue) ? total : total + actualValue;
    }, 0);
}

/** Vs par for one tournament round row (`linescores` entry for `roundPeriod`). */
function vsParForRound(linescores, roundPeriod) {
    const rp = Number(roundPeriod);
    if (!Number.isFinite(rp) || rp < 1) return null;
    const line = (linescores || []).find(
        (ls) => Number(ls.period) === rp,
    );
    const dv = line?.displayValue;
    if (dv == null || dv === "") return null;
    if (dv === "E" || dv === "-") return 0;
    const n = parseInt(dv, 10);
    return Number.isNaN(n) ? null : n;
}

function hasPlayedAnyHole(player) {
    return Boolean(
        player.linescores?.some((scoreCard) => {
            const value = scoreCard?.displayValue;
            return (
                value !== undefined &&
                value !== null &&
                value !== "" &&
                value !== "-"
            );
        }),
    );
}

/**
 * ESPN gives scheduled (not-started) players position.id "0", which sorts ahead
 * of the field and breaks prize mapping. Remap them to the same position tier
 * as starters currently at even par (0 vs par from linescores).
 */
function resolveEvenParPositionId(playersNotCut) {
    const evenStarters = playersNotCut.filter(
        (p) =>
            hasPlayedAnyHole(p) &&
            totalToParFromLinescores(p) === 0 &&
            parseInt(p.status?.position?.id, 10) > 0,
    );
    if (evenStarters.length === 0) return null;
    return String(
        Math.min(
            ...evenStarters.map((p) => parseInt(p.status.position.id, 10)),
        ),
    );
}

function resolveFallbackPositionNum(playersNotCut) {
    const nums = playersNotCut
        .map((p) => parseInt(p.status?.position?.id, 10))
        .filter((n) => n > 0);
    if (nums.length === 0) return 1;
    return Math.max(...nums) + 1;
}

function effectivePositionId(player, evenParPosId, fallbackPosNum) {
    const raw = player.status?.position?.id;
    const posNum = parseInt(raw, 10);
    const played = hasPlayedAnyHole(player);
    if (!played || !raw || posNum <= 0) {
        return evenParPosId != null ? evenParPosId : String(fallbackPosNum);
    }
    return String(raw);
}

/** Fresh entrants each run — never mutate module-level picks.json */
export function createInitialRankings() {
    return picksTemplate.map((u) => ({
        name: u.name,
        picks: [...u.picks],
        summary: [],
        totalMoney: 0,
    }));
}

/**
 * Pure: builds money breakdown + per-entrant prize summaries from API + purse table.
 */
export function computeLeaderboardState(
    response,
    prizeMoneyArr,
    holeDetailsByAthleteId = {},
) {
    const users = createInitialRankings();

    const eventRoot = response.events[0];
    const competition = eventRoot.competitions[0];
    const allPlayers = competition.competitors;
    const playersNotCut = allPlayers.filter(
        (player) => player.status?.type?.name !== "STATUS_CUT",
    );
    const eventId =
        eventRoot.id != null ? String(eventRoot.id) : "";
    const rawPeriod = Number(competition.status?.period);
    const competitionPeriod =
        Number.isFinite(rawPeriod) && rawPeriod > 0 ? rawPeriod : 1;

    const playerCatalog = Object.fromEntries(
        playersNotCut.map((p) => {
            const name = p.athlete.displayName;
            return [
                name,
                {
                    athleteId: String(p.athlete.id),
                    displayName: name,
                    avatarURL: p.athlete.headshot?.href ?? null,
                    country: p.athlete.flag?.alt ?? null,
                    flagURL: p.athlete.flag?.href ?? null,
                    scoreDisplay: p.score?.displayValue ?? null,
                    scoreVsPar: hasPlayedAnyHole(p)
                        ? totalToParFromLinescores(p)
                        : null,
                    currentRoundVsPar: vsParForRound(
                        p.linescores,
                        competitionPeriod,
                    ),
                },
            ];
        }),
    );

    const cutPickNames = [
        ...new Set(
            allPlayers
                .filter((p) => p.status?.type?.name === "STATUS_CUT")
                .map((p) => p.athlete?.displayName)
                .filter(Boolean),
        ),
    ];

    const evenParPosId = resolveEvenParPositionId(playersNotCut);
    const fallbackPosNum = resolveFallbackPositionNum(playersNotCut);
    const leaderBoard = [...playersNotCut].sort((a, b) => {
        const pa = parseInt(
            effectivePositionId(a, evenParPosId, fallbackPosNum),
            10,
        );
        const pb = parseInt(
            effectivePositionId(b, evenParPosId, fallbackPosNum),
            10,
        );
        if (pa < pb) return -1;
        if (pa > pb) return 1;
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

    const moneyBreakdownRework = leaderBoard.reduce(
        (summary, player, currentIndex) => {
            const posId = effectivePositionId(
                player,
                evenParPosId,
                fallbackPosNum,
            );
            const hasPlayedHoles = hasPlayedAnyHole(player);

            summary[posId] = {
                amountTotal:
                    (summary[posId]?.amountTotal || 0) +
                    (prizeMoneyArr[currentIndex] || 0),
                sharedBetween: (summary[posId]?.sharedBetween || 0) + 1,
                score: player.linescores.reduce((total, scores) => {
                    if (!scores.displayValue) return total + 0;
                    const actualValue =
                        scores.displayValue === "E" || scores.displayValue === "-"
                            ? 0
                            : parseInt(scores.displayValue, 10);
                    return total + actualValue;
                }, 0),
                players: summary[posId]?.players || [],
                index: currentIndex,
            };

            summary[posId].amountEach =
                summary[posId].amountTotal / summary[posId].sharedBetween;

            const aid = String(player.athlete.id);
            const linescoresForm = holeDetailsByAthleteId[aid] ?? null;
            const { roundThruLabel } = buildRoundDisplayLines(player.status);
            const completedRoundGross = completedRoundGrossStrokes(
                player.status,
                player.linescores,
            );
            const recentThreeVsPar = linescoresForm?.lastThreeVsPar ?? null;
            const vsParNum = totalToParFromLinescores(player);
            const scoreVsPar = hasPlayedHoles ? vsParNum : null;
            summary[posId].players.push({
                ...player.athlete,
                hasPlayedHoles: Boolean(hasPlayedHoles),
                roundThruLabel,
                completedRoundGross,
                recentThreeVsPar,
                scoreVsPar,
            });

            return summary;
        },
        {},
    );

    Object.keys(moneyBreakdownRework).forEach((key) => {
        const place = moneyBreakdownRework[key];
        place.players.forEach((player) => {
            users.forEach((user) => {
                if (user.picks.indexOf(player.displayName) !== -1) {
                    user.totalMoney += place.amountEach;
                    user.summary.push({
                        amount: place.amountEach,
                        name: player.displayName,
                        avatarURL: player.headshot?.href,
                        roundThruLabel: player.roundThruLabel,
                        completedRoundGross: player.completedRoundGross,
                        recentThreeVsPar: player.recentThreeVsPar,
                        scoreVsPar: player.scoreVsPar,
                    });
                }
            });
        });
    });

    users.sort((a, b) => {
        if (a.totalMoney > b.totalMoney) return -1;
        if (a.totalMoney < b.totalMoney) return 1;
        return 0;
    });

    return {
        rankings: users,
        moneyBreakdown: moneyBreakdownRework,
        playerCatalog,
        cutPickNames,
        eventId,
        competitionPeriod,
        pairingGroups: buildPairingGroups(competition),
    };
}
